/**
 * Hook for uploading files using CCMS SDK FileService
 * Handles file upload to S3 with progress tracking
 */

import { useState, useCallback, useRef } from 'react';
import { useFileService } from '@actschurch/ccms-sdk';
import { validateMimeType, validateFileSize } from '../../utils/validation';
import type { FileInfo, UploadProgress } from '../types';

export interface UseFileUploadOptions {
  domain: string;
  recordId?: string;
  maxSize?: number;
  allowedTypes?: string[];
  onComplete?: (files: FileInfo[]) => void;
  onError?: (error: Error) => void;
  onProgress?: (progress: UploadProgress[]) => void;
}

export interface UseFileUploadReturn {
  files: File[];
  uploading: boolean;
  progress: UploadProgress[];
  upload: (files: FileList | File[]) => Promise<void>;
  cancel: () => void;
  error: Error | null;
  uploadedFiles: FileInfo[];
}

/**
 * Hook for uploading files with CCMS SDK FileService
 * Uses S3 presigned URLs for direct upload
 *
 * @example
 * ```tsx
 * const { upload, uploading, progress, error } = useFileUpload({
 *   domain: 'User',
 *   recordId: 'user-123',
 *   maxSize: 10 * 1024 * 1024, // 10MB
 *   allowedTypes: ['image/*', 'application/pdf'],
 *   onComplete: (files) => console.log('Uploaded:', files),
 * });
 *
 * // In your component
 * <input type="file" onChange={(e) => upload(e.target.files)} />
 * ```
 */
export function useFileUpload(options: UseFileUploadOptions): UseFileUploadReturn {
  const fileService = useFileService();
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<FileInfo[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const upload = useCallback(
    async (fileList: FileList | File[]) => {
      const fileArray = Array.from(fileList);
      setFiles(fileArray);
      setUploading(true);
      setError(null);

      // Initialize progress for each file
      const initialProgress: UploadProgress[] = fileArray.map((file) => ({
        fileName: file.name,
        progress: 0,
        status: 'pending' as const,
      }));
      setProgress(initialProgress);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const uploaded: FileInfo[] = [];

        for (let i = 0; i < fileArray.length; i++) {
          const file = fileArray[i];

          // Check if cancelled
          if (controller.signal.aborted) {
            throw new Error('Upload cancelled');
          }

          // Update progress: starting upload
          setProgress((prev) =>
            prev.map((p, idx) =>
              idx === i ? { ...p, status: 'uploading' as const, progress: 10 } : p
            )
          );

          // Validate file size
          if (options.maxSize && !validateFileSize(file, options.maxSize)) {
            throw new Error(
              `File "${file.name}" exceeds maximum size of ${Math.round(options.maxSize / 1024 / 1024)}MB`
            );
          }

          // Validate file type
          if (options.allowedTypes && !validateMimeType(file, options.allowedTypes)) {
            throw new Error(`File type "${file.type}" is not allowed for "${file.name}"`);
          }

          // Update progress: validated, starting S3 upload
          setProgress((prev) =>
            prev.map((p, idx) =>
              idx === i ? { ...p, progress: 30 } : p
            )
          );

          // Upload using SDK FileService (handles S3 presigned URLs)
          const result = await fileService.uploadFileAuto(file, {
            domain: options.domain,
            recordId: options.recordId || 'general',
          });

          // Update progress: processing
          setProgress((prev) =>
            prev.map((p, idx) =>
              idx === i
                ? { ...p, status: 'processing' as const, progress: 90 }
                : p
            )
          );

          // Create file record from API response
          const fileRecord: FileInfo = {
            id: result.fileId,
            fileName: file.name,
            mimeType: file.type,
            fileSize: file.size,
            url: result.url,
            domain: options.domain,
            recordId: options.recordId,
            status: 'completed',
            uploadedAt: new Date().toISOString(),
          };

          uploaded.push(fileRecord);

          // Update progress: completed
          setProgress((prev) =>
            prev.map((p, idx) =>
              idx === i ? { ...p, status: 'completed' as const, progress: 100 } : p
            )
          );

          // Call progress callback
          options.onProgress?.(progress);
        }

        setUploadedFiles(uploaded);
        options.onComplete?.(uploaded);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);

        // Mark failed uploads in progress
        setProgress((prev) =>
          prev.map((p) =>
            p.status === 'uploading' || p.status === 'processing'
              ? { ...p, status: 'failed' as const, error: error.message }
              : p
          )
        );

        options.onError?.(error);
      } finally {
        setUploading(false);
        abortControllerRef.current = null;
      }
    },
    [options, fileService, progress]
  );

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setUploading(false);
    setProgress((prev) =>
      prev.map((p) =>
        p.status === 'uploading' || p.status === 'processing'
          ? { ...p, status: 'failed' as const, error: 'Upload cancelled' }
          : p
      )
    );
  }, []);

  return {
    files,
    uploading,
    progress,
    upload,
    cancel,
    error,
    uploadedFiles,
  };
}
